import {createStore} from '../alt';
import moment from 'moment';

import SingleObjectResource, {WithoutBaseResource, InvalidResponseCode} from '../resources/GenericResource';

import CurrentUserActions from '../actions/CurrentUserActions';
import ShiftActions from '../actions/ShiftActions';

import doRedirect from '../utils/doRedirect';


const ShiftSource = {
    loadMoreProShifts() {
        return {
            remote(state, next_page) {
                return new WithoutBaseResource(next_page).fetch();
            },

            success: ShiftActions.getMoreProShifts,
            error: CurrentUserActions.pageFault
        };

    },

    getProShifts() {
        return {
            remote(state, queryString) {
                return new SingleObjectResource(DJ_CONST.API.shiftProList + queryString).fetch();
            },

            success: ShiftActions.getProSuccess,
            error: ShiftActions.getProFailed
        };

    },

    getMyShifts() {
        return {
            remote(state, dateInDesiredMonth) {
                const start_time = dateInDesiredMonth.startOf('month');
                const end_time = moment(start_time).add(1, 'months');
                const queryString = `?start_time=${start_time.format("YYYY-MM-DD HH:mm")}&end_time=${end_time.format("YYYY-MM-DD HH:mm")}`;
                return new SingleObjectResource(DJ_CONST.API.shiftProHired + queryString).fetch();
            },

            success: ShiftActions.getMyShiftsSuccess,
            error: ShiftActions.getMyShiftsFailed
        };

    },

    getMaxRate() {
        return {
            remote() {
                return new SingleObjectResource(DJ_CONST.API.shiftProList + "/max_rate").fetch();
            },

            local(state) {
                // So we don't do the call again when rendering clientside
                return state.shiftCount ? state : null;
            },

            success: ShiftActions.getMaxRateSuccess,
            error: ShiftActions.getMaxRateFailed
        };
    },

    getLocations() {
        return {
            remote(state, data) {
                return new SingleObjectResource(DJ_CONST.API.shiftProList + "/locations").post(null, data);
            },

            local(state) {
                // So we don't do the call again when rendering clientside
                return state.shiftCount ? state : null;
            },

            success: ShiftActions.getLocationsSuccess,
            error: CurrentUserActions.pageFault
        };
    },

    getShiftsToFinish() {
        return {
            remote(state) {
                return new SingleObjectResource(DJ_CONST.API.shiftFinishedShifts).fetch();
            },

            success: ShiftActions.getShiftsToFinishSuccess,
            error: CurrentUserActions.pageFault
        };
    },

    getShifts() {
        return {
            remote(state, dateInDesiredMonth) {
                const start_time = dateInDesiredMonth.startOf('month');
                const end_time = moment(start_time).add(1, 'months');
                const queryString = `?start_time=${start_time.format("YYYY-MM-DD HH:mm")}&end_time=${end_time.format("YYYY-MM-DD HH:mm")}`;
                return new SingleObjectResource(DJ_CONST.API.shiftList + queryString).fetch();
            },

            success: ShiftActions.getSuccess,
            error: CurrentUserActions.pageFault
        };
    },

    getShiftsFullReport() {
        return {
            remote(state, dateInDesiredMonth) {
                const start_time = dateInDesiredMonth.startOf('month');
                const end_time = moment(start_time).add(1, 'months');
                const queryString = `?start_time=${start_time.format("YYYY-MM-DD HH:mm")}&end_time=${end_time.format("YYYY-MM-DD HH:mm")}`;
                return new SingleObjectResource(DJ_CONST.API.shiftFullReport + queryString).fetch();
            },

            success: ShiftActions.getFullReportSuccess,
            error: CurrentUserActions.pageFault
        };
    },

    getShift() {
        return {
            remote(state, shiftId) {
                return new SingleObjectResource(DJ_CONST.API.shiftDetail).fetch({pk: shiftId});
            },

            success: ShiftActions.getDetailSuccess,
            error: ShiftActions.getDetailFailed
        }
    },

    hasShifts() {
        return {
            remote() {
                return new SingleObjectResource(DJ_CONST.API.shiftList + "/check").fetch();
            },

            local(state) {
                // So we don't do the call again when rendering clientside
                return state.shiftCount ? state : null;
            },

            success: ShiftActions.checkSuccess,
            error: CurrentUserActions.pageFault
        };
    },

    postUpdateToCandidates() {
        return {
            remote(state, shift_id, message) {
                return new SingleObjectResource(DJ_CONST.API.shiftPostUpdate).post({pk: shift_id}, {
                    message: message
                });
            },

            success: ShiftActions.postUpdateToCandidatesSuccess,
            error: ShiftActions.postUpdateToCandidatesError
        };
    },

    sendHiresList() {
        return {
            remote(state, shift) {
                return new SingleObjectResource(DJ_CONST.API.shiftSendHiresList).post({pk: shift.id});
            },

            success: ShiftActions.sendHiresListSuccess,
            error: ShiftActions.sendHiresListError
        };
    },

    finishShift() {
        return {
            remote(state, shiftId, candidateHours, type) {
                return new SingleObjectResource(DJ_CONST.API.shiftFinish).post({pk: shiftId}, {candidateHours, type});
            },

            success: ShiftActions.finishSuccess,
            error: CurrentUserActions.pageFault
        };
    },

    cancelShift() {
        return {
            remote(state, shiftId, message) {
                return new SingleObjectResource(DJ_CONST.API.shiftCancel).post({pk: shiftId}, {message: message});
            },

            success: ShiftActions.cancelSuccess,
            error: CurrentUserActions.pageFault
        };
    },

    updatePosition() {
        return {
            remote(state, shiftId, data) {
                return new SingleObjectResource(DJ_CONST.API.shiftUpdatePosition).post({pk: shiftId}, data);
            },

            success: ShiftActions.getUpdateRateSuccess,
            error: CurrentUserActions.pageFault
        };
    }
};

class ShiftStore {
    constructor() {
        this.next_page = null;
        this.shifts = [];
        this.shifts_pro = [];
        this.proShiftsLoaded = false;
        this.detailShift = {};
        this.detailShiftError = null;
        this.detailShiftReceived = false;
        this.postUpdateToCandidatesError = null;
        this.createDate = '';
        this.shiftsToFinish = [];
        this.max_rate = null;
        this.searchResults = {};
        this.myShifts = [];
        this.dummyShift = {};

        this.detailShiftSaved = false;
        this.rateSaved = false;

        this.createError = {};
        this.stepOneData = {};
        this.stepTwoData = {};
        this.stepThreeData = {};

        this.hiresListSuccess = null;

        this.shiftCount = 0;
        this.exportAsync(ShiftSource);
        this.bindActions(ShiftActions);

        this.shiftCancelled = null;

        this.fullReport = {};
    }

    onClearShifts() {
        this.setState({shifts: []});
    }

    onClearFinishedShifts() {
        this.setState({shiftsToFinish: []});
    }

    onDuplicateShift(dummyShift) {
        this.setState({dummyShift});
    }

    onGetUpdateRateSuccess() {
        this.setState({rateSaved: true});
        setTimeout(x => {this.setState({rateSaved: false})}, 5000);
    }

    onClearDetailShift() {
        this.setState({detailShift: {}});
    }

    onGetLocationsSuccess(searchResults) {
        this.setState({searchResults});
    }

    onGetMaxRateSuccess(max_rate) {
        this.setState({max_rate});
    }

    onGetMyShiftsSuccess(myShifts) {
        this.setState({myShifts});
    }

    onGetMoreProShifts(shifts) {
        const shifts_pro = this.shifts_pro.concat(shifts.results);
        const next_page = shifts.next ? shifts.next : null;
        this.setState({shifts_pro, page: this.page+1, next_page, proShiftsLoaded: true});
    }

    onGetProSuccess(shifts) {
        const shifts_pro = shifts.results;
        const next_page = shifts.next ? shifts.next : null;
        this.setState({shifts_pro, next_page, proShiftsLoaded: true});
    }

    onGetSuccess(shifts) {
        this.setState({shifts, detailShiftSaved: false});
    }

    onGetFullReportSuccess(fullReport) {
        this.setState({fullReport});
    }

    onGetShiftsToFinishSuccess(shiftsToFinish) {
        this.setState({shiftsToFinish: shiftsToFinish.data});
    }

    onGetDetailFailed(detailShiftError) {
        this.setState({detailShiftError});
    }

    onGetDetailSuccess(detailShift) {
        this.setState({detailShift, createError: {}, detailShiftSaved: false, detailShiftReceived: true});
    }

    onSaveStepOneData(stepOneData) {
        this.setState({stepOneData});
    }

    onSaveStepTwoData(stepTwoData) {
        this.setState({stepTwoData});
    }

    onCreateShift(x) {
        const id = x.result.data.id;
        doRedirect(x.result.router, 'shiftcompleted', {shiftId: id});
    }

    onEditShift(x) {
        const id = x.result.id;
        doRedirect(x.result.router, 'shift', {shiftId: id});
    }

    onCheckSuccess(shiftCount) {
        this.setState({shiftCount});
    }

    onMergeErrorsIntoState(errors) {
        this.setState({createError: errors});
    }

    onSetCreateShiftDate(createDate) {
        this.setState({createDate});
    }

    onFailCreateShift(errors) {
        this.setState({createError: errors.updateError});
        if (typeof errors.stepChangeCallback == 'function') {
            errors.stepChangeCallback.call(this, 1, true);
        }
    }

    onPostUpdateToCandidatesError(postUpdateToCandidatesError) {
        this.setState({postUpdateToCandidatesError});
    }

    onSaveDraft(x) {
        this.setState({detailShiftSaved: true});
        doRedirect(x.result.router, 'agenda', null, {'draft_saved': 'true'});
    }

    onSendHiresListSuccess(data) {
        this.setState({hiresListSuccess: data});
    }

    onClearHires() {
        this.setState({hiresListSuccess: null});
    }

    // We need it because we got errors while editing shift and adding new contacts. Also publishing a draft failed.
    onOptimisticShiftUpdate(contacts) {
        this.setState({
            detailShift: {...this.detailShift, contacts}
        });
    }

    onCancelSuccess(data) {
        this.setState({
            shiftCancelled: data.shift_id,
            detailShift: {}
        });
    }

    onClearCreation() {
        const newState = {
            createError: {},
            stepOneData: {},
            stepTwoData: {},
            stepThreeData: {}
        }
        this.setState(newState);
    }
}

export default createStore(ShiftStore, 'ShiftStore', module);



/** WEBPACK FOOTER **
 ** ./app/src/stores/ShiftStore.js
 **/